import React, { useMemo, useRef } from "react";
import PerformanceAttribution from "../../../common/PerformanceAttribution";
import { Btn, EmptyState, Loading, NoticeState } from "../../../ForBrands/ui/BrandUI";
import { CollapseAllButton, SectionDivider, useCollapseAll } from "../../../ForBrands/ui/CollapsibleCard";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { ARCHETYPE_META, NO_30D_HISTORY, biggestSwing, fmtSigned, swingPoints } from "../coachUtils";
import { fullNameOf } from "../../../ForBrands/brandDealUtils";
import { fmtIndex10 } from "../../../../utils/indexScore";
import PlayerInsight from "./PlayerInsight";
import TeamArchetypeCard from "./TeamArchetypeCard";
import TeamSnapshotCard from "./TeamSnapshotCard";
import {
  PERF_CARD_IDS,
  RETRY_REASONS,
  coverageLine,
  makeupMissingNote,
  makeupOf,
  partialNote,
  peerOf,
  perfCardIds,
  perfStateOf,
  reasonCopy,
  squadLabelOf,
  staleNote,
  teamPerformanceAttributed,
} from "./teamPerformanceModel";
import GameLogCard from "./perf/GameLogCard";
import FourFactorsCard from "./perf/FourFactorsCard";
import NetFormCard from "./perf/NetFormCard";
import ShotProfileCard from "./perf/ShotProfileCard";
import TeamConsistencyCard from "./perf/TeamConsistencyCard";
import RosterGapsCard from "./perf/RosterGapsCard";
import RosterMixCard from "./perf/RosterMixCard";
import PositionDepthCard from "./perf/PositionDepthCard";
import RotationCard from "./perf/RotationCard";
import PlayerSpotlight from "./perf/PlayerSpotlight";

// Team · Performance (Figma 02b; Jayson's team dashboard, BUILD_SPEC B7-B10):
//  - what the numbers cover and, on desktop, one Collapse all / Expand all
//    control for every card on the tab (phones fold card by card);
//  - a row: the Team Performance Snapshot (~60%) beside the Team Archetype
//    (~40%; the app's own card and rules, from the team style);
//  - "Game by game": the Game Log; "Game profile": Four Factors | Form, then
//    Shot Profile & Scoring Mix | Consistency; "Team makeup and missing
//    pieces": What the Roster Is Missing, Roster Mix vs Peers | Position
//    Depth, then the Rotation;
//  - "Player spotlight": most minutes, leading scorer and highest-rated
//    from the rotation, and under it the three CACHE insights (Highest
//    CacheScore, Biggest swing, Off-court leader);
//  - last, once, the data attribution line while the league feed's data is
//    on the tab (common/PerformanceAttribution, Kobi 10/21;
//    teamPerformanceAttributed).
// The game analytics come from coach/team/performance/ (useTeamPerformance
// on the page): while they load the snapshot keeps its place with
// placeholders and its title, and the archetype card and its info button
// stay as they are; without them the snapshot shows today's league box
// scores and one plain note says why. Every card is collapsible and open
// by default; nothing is persisted. The biggest swing is the Swing
// Composition dot farthest from the diagonal; without one, the note says
// whether nobody is plotted yet or everyone sits on the diagonal. The
// off-court leader's score reads out of 10 ("9.0/10"), the CacheScore
// leader's out of 100, as everywhere else. Phones (2409:907) stack it all
// in one column; the snapshot and archetype cards are keyed, so an open
// archetype sheet survives a turn to landscape.

// Rendered inside the tab's one status region (AnalyticsStatus), which
// announces each change.
const PendingSections = ({ building }) =>
  building ? (
    <NoticeState>Building this squad's game analytics. The first time takes a few seconds.</NoticeState>
  ) : (
    <Loading label="Loading game analytics..." />
  );

const UnavailableSections = ({ reason, onRetry }) => {
  const copy = reasonCopy(reason);
  if (!copy) return null;
  const action =
    RETRY_REASONS.has(reason) && onRetry ? (
      <Btn variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Btn>
    ) : null;
  return <EmptyState action={action}>{copy}</EmptyState>;
};

// Loading, building, then why there are no game analytics (and Try again):
// one polite live region (role="status") that stays mounted across those
// states, so a screen reader hears each change without a focus move.
// Try again unmounts its own button, so it moves the keyboard focus to this
// region (now saying "Loading game analytics..."), never back to the top of
// the page. Nothing for a reason that hides the section (football).
const AnalyticsStatus = ({ state, reason, onRetry }) => {
  const region = useRef(null);
  const pending = state === "loading" || state === "building";
  if (!pending && !(state === "unavailable" && reasonCopy(reason))) return null;
  const retry = onRetry
    ? () => {
        onRetry();
        region.current?.focus();
      }
    : undefined;
  return (
    <div role="status" ref={region} tabIndex={-1} className="focus:outline-none">
      {pending ? <PendingSections building={state === "building"} /> : <UnavailableSections reason={reason} onRetry={retry} />}
    </div>
  );
};

const GameSections = ({ perf, payload, makeup, card, onOpenProfile }) => {
  const phone = usePhoneTheme();
  const pair = phone ? "flex flex-col gap-[18px]" : "grid items-start gap-4 lg:grid-cols-2";
  const squad = squadLabelOf(perf);
  const peer = peerOf(perf);
  return (
    <>
      <SectionDivider label="Game by game" className="mt-2" />
      <GameLogCard payload={payload} collapse={card(PERF_CARD_IDS.gameLog)} />
      <SectionDivider label="Game profile" className="mt-2" />
      <div className={pair}>
        <FourFactorsCard payload={payload} squadLabel={squad} collapse={card(PERF_CARD_IDS.fourFactors)} />
        <NetFormCard payload={payload} collapse={card(PERF_CARD_IDS.form)} />
      </div>
      <div className={pair}>
        <ShotProfileCard payload={payload} peer={peer} collapse={card(PERF_CARD_IDS.shot)} />
        <TeamConsistencyCard payload={payload} peer={peer} collapse={card(PERF_CARD_IDS.consistency)} />
      </div>
      <SectionDivider label="Team makeup and missing pieces" className="mt-2" />
      {makeup ? (
        <>
          <RosterGapsCard makeup={makeup} peerLabel={peer?.label} collapse={card(PERF_CARD_IDS.gaps)} />
          <div className={pair}>
            <RosterMixCard makeup={makeup} squadLabel={squad} collapse={card(PERF_CARD_IDS.mix)} />
            <PositionDepthCard makeup={makeup} collapse={card(PERF_CARD_IDS.depth)} />
          </div>
          <RotationCard makeup={makeup} onOpenProfile={onOpenProfile} collapse={card(PERF_CARD_IDS.rotation)} />
        </>
      ) : (
        <EmptyState>{makeupMissingNote(perf)}</EmptyState>
      )}
    </>
  );
};

const TeamPerformanceTab = ({
  players,
  performance,
  style,
  styleLoading,
  perf = null,
  perfLoading = false,
  perfBuilding = false,
  onRetryPerf,
  onOpenProfile,
}) => {
  const phone = usePhoneTheme();
  const highlights = performance?.highlights || {};
  const { highest, offCourtLeader } = highlights;
  const { swing, plotted } = useMemo(() => ({ swing: biggestSwing(players), plotted: swingPoints(players).length > 0 }), [players]);
  const swingMeta = swing && ARCHETYPE_META[swing.archetype];

  const state = perfStateOf({ perf, loading: perfLoading, building: perfBuilding });
  const ready = state === "ready";
  const payload = ready ? perf.payload : null;
  const makeup = makeupOf(payload);
  const collapse = useCollapseAll(perfCardIds(state, payload));
  const card = (id) => collapse.cardProps(id);
  const coverage = ready ? coverageLine(perf) : null;
  // A saved copy, and some numbers missing this time: one notice.
  const notice = ready ? [staleNote(perf), partialNote(perf)].filter(Boolean).join(" ") : null;

  const snapshot = (
    <TeamSnapshotCard key="snapshot" state={state} perf={perf} style={style} styleLoading={styleLoading} collapse={card(PERF_CARD_IDS.snapshot)} />
  );
  // With the analytics on screen the snapshot ranks against their peer
  // group, so the archetype names its own (the team style's league cohort).
  const archetype = <TeamArchetypeCard key="archetype" style={style} loading={styleLoading} collapse={card(PERF_CARD_IDS.archetype)} showCohort={ready} />;
  return (
    <>
      {(!phone || coverage) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {coverage ? <p className="min-w-0 break-words bm-body-s text-bm-muted">{coverage}</p> : <span aria-hidden="true" />}
          <CollapseAllButton control={collapse} />
        </div>
      )}
      {notice && <NoticeState role="status">{notice}</NoticeState>}
      <div className={phone ? "flex flex-col gap-[18px]" : "grid items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"}>
        {[snapshot, archetype]}
      </div>
      {ready && <GameSections perf={perf} payload={payload} makeup={makeup} card={card} onOpenProfile={onOpenProfile} />}
      <AnalyticsStatus state={state} reason={perf?.reason} onRetry={onRetryPerf} />
      <SectionDivider label="Player spotlight" className="mt-2" />
      {ready && <PlayerSpotlight makeup={makeup} />}
      <div className={phone ? "flex flex-col gap-3" : "grid gap-4 md:grid-cols-3"}>
        <PlayerInsight
          eyebrow="Highest CacheScore™"
          player={highest}
          title={highest && `${fullNameOf(highest)} · ${highest.score}`}
          chip={highest?.trend != null ? `${fmtSigned(highest.trend)} pts in 30d` : NO_30D_HISTORY}
          emptyNote="Appears once players activate"
        />
        <PlayerInsight
          eyebrow="Biggest swing"
          player={swing?.player}
          title={swing && fullNameOf(swing.player)}
          chip={swingMeta && `${swingMeta.label} · ${swing.signal}`}
          chipDot={swingMeta?.color}
          emptyNote={plotted ? "No swing on the roster yet" : "Appears once players carry a CacheValue and NILx"}
        />
        <PlayerInsight
          eyebrow="Off-court leader"
          player={offCourtLeader}
          title={offCourtLeader && `${fullNameOf(offCourtLeader)} · ${fmtIndex10(offCourtLeader.offCourt, { suffix: true })}`}
          chip="Highest off-court impact"
          emptyNote="No off-court scores yet"
        />
      </div>
      {teamPerformanceAttributed({ state, style, styleLoading }) && <PerformanceAttribution />}
    </>
  );
};

export default TeamPerformanceTab;
